// V141.16 AUGMENT: ORIGINAL DARK MODE INTERFACE - PRESERVE UX
// V141.21 BACKEND FIX: Only unifiedPhotoService.ts modified for WebP + public_id
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Check, Loader2, RotateCcw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { fixedUnifiedPhotoService } from '@/services/FixedUnifiedPhotoService';
import { usePhotoContext } from '@/contexts/PhotoContext';
import { SelfiePermissionScreen } from '@/components/camera/SelfiePermissionScreen';
import { Capacitor } from '@capacitor/core';
import { Camera as CapacitorCamera, CameraResultType, CameraSource, CameraDirection, CameraPermissionType } from '@capacitor/camera';
import { normalizeProfilePhoto } from '@/utils/normalizeProfilePhoto';
import { debugLog, debugInfo, debugWarn, debugError, debugLogger } from '@/utils/debugLogger';
import { resolveDocumentDirection } from '@/i18n/languages';
import {
  calculateCoverSourceCrop,
  isMirroredPreview,
  PROFILE_PHOTO_HEIGHT,
  PROFILE_PHOTO_WIDTH,
} from '@/utils/cameraCrop';
import '@/styles/video-mirror.css';

interface ProfilePhotoCaptureProps {
  onComplete: (photoUrl: string) => void;
  onSkip: () => void;
  userName?: string; // V207.23: Optional userName prop for onboarding (Step 1 name not yet in DB)
  onFileSelected?: (file: File, previewUrl: string, dataUrl?: string) => void; // V275.11: Added dataUrl parameter
  onImageCaptured?: (file: File, previewUrl: string) => void; // V242: For onboarding - pass captured image without uploading
}

const getErrorName = (error: unknown): string | undefined =>
  error instanceof Error ? error.name : undefined;

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const ProfilePhotoCapture: React.FC<ProfilePhotoCaptureProps> = ({
  onComplete,
  onSkip,
  userName: userNameProp, // V207.23: Rename to avoid conflict with local userName variable
  onFileSelected, // Android: Callback to trigger replace dialog
  onImageCaptured // V242: For onboarding - pass captured image without uploading
}) => {
  const { t, i18n } = useTranslation();
  const direction = resolveDocumentDirection(i18n.resolvedLanguage || i18n.language);
  const { user } = useAuth();
  const { updateUserPhoto } = usePhotoContext(); // V150.17: Add photo context
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cropFrameRef = useRef<HTMLDivElement>(null);

  // V141.16: Original state management for dark mode interface
  const isAndroid = Capacitor.getPlatform() === 'android';
  const [showPermissionScreen, setShowPermissionScreen] = useState(true); // Show permission screen first
  const [permissionDenied, setPermissionDenied] = useState(false); // Track if permission permanently denied
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false); // Only activate after permission granted
  const [capturedImageData, setCapturedImageData] = useState<string | null>(null);
  const [isInCaptureMode, setIsInCaptureMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [photoVersion, setPhotoVersion] = useState(0);
  const uploadAbortController = useRef<AbortController | null>(null); // For cancel during upload

  // V150.28: Hide bottom navigation for fullscreen camera experience
  useEffect(() => {
    // Find and hide bottom navigation
    const bottomNav =
      document.querySelector('[role="navigation"]') ||
      document.querySelector('.fixed.bottom-0') ||
      document.querySelector('nav[class*="bottom"]') ||
      document.querySelector('[class*="bottom"][class*="nav"]');

    let originalDisplay = '';

    if (bottomNav) {
      originalDisplay = (bottomNav as HTMLElement).style.display;
      (bottomNav as HTMLElement).style.display = 'none';
      console.log('🚫 V150.28: Bottom navigation hidden for fullscreen camera');
    } else {
      console.warn('⚠️ V150.28: Bottom navigation not found');
    }

    // Cleanup: Restore bottom navigation when unmounting
    return () => {
      if (bottomNav) {
        (bottomNav as HTMLElement).style.display = originalDisplay;
        console.log('✅ V150.28: Bottom navigation restored');
      }
    };
  }, []);

  // Request camera permission and start camera
  // On Android: Use Capacitor Camera plugin (native camera UI)
  // On Web/iOS: Use web getUserMedia (custom camera UI)
  const requestCameraPermissionAndStart = useCallback(async () => {
    try {
      setShowPermissionScreen(false);
      setPermissionDenied(false);
      console.log('📷 Requesting camera permission...', { platform: Capacitor.getPlatform() });
      
      // V276.9: Android uses same live preview UI as web/iOS
      if (isAndroid) {
        try {
          console.log('🎯🎯🎯 V276.9: Android selfie with LIVE PREVIEW 🎯🎯🎯');

          // Check/request camera permission via Capacitor
          const permissionStatus = await CapacitorCamera.checkPermissions();
          console.log('🎯 V276.9: Permission status:', permissionStatus.camera);

          let hasPermission = permissionStatus.camera === 'granted';

          if (!hasPermission) {
            const requestResult = await CapacitorCamera.requestPermissions({
              permissions: ['camera']
            });
            console.log('🎯 V276.9: Permission request result:', requestResult.camera);
            hasPermission = requestResult.camera === 'granted';
          }

          if (!hasPermission) {
            setPermissionDenied(true);
            setShowPermissionScreen(true);
            return;
          }

          console.log('🎯 V276.9: Permission granted, opening camera stream...');

          // V276.9: Use web getUserMedia - same as web/iOS flow
          let mediaStream: MediaStream;
          try {
            mediaStream = await navigator.mediaDevices.getUserMedia({
              video: {
                facingMode: 'user',  // Front camera
                width: { ideal: 1080 },
                height: { ideal: 1350 }
              },
              audio: false
            });
            console.log('🎯 V276.9: ✅ Camera stream obtained (FRONT camera)');
          } catch (mediaError: unknown) {
            console.error('🎯 V276.9: ❌ getUserMedia failed:', mediaError);
            setShowPermissionScreen(true);
            const errorName = getErrorName(mediaError);
            if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
              setPermissionDenied(true);
            } else {
              toast.error(t('camera.cameraAccessFailed'));
            }
            return;
          }

          // V276.9: Set stream to state - this triggers the video preview UI
          setStream(mediaStream);
          setIsCameraActive(true);
          console.log('🎯 V276.9: ✅ Stream set to state, video preview will show');

          // V276.9: Assign stream to video element
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
            console.log('🎯 V276.9: ✅ Stream assigned to video element');
          }

          // The video preview UI will now render (removed the isAndroid return null check)

        } catch (error: unknown) {
          console.error('🎯 V276.9: ❌ Camera setup failed:', error);
          setShowPermissionScreen(true);
          setPermissionDenied(true);
        }
      } else {
        // Web/iOS: Use web getUserMedia API
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user', // Front camera
            // Use 4:3 ratio (best Android support) then crop to 1:1 visually
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });
        
        setStream(mediaStream);
        setIsCameraActive(true);
        
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          console.log('✅ Camera started successfully (web)');
        }
      }
    } catch (error: unknown) {
      console.error('❌ Camera access failed:', error);
      setShowPermissionScreen(true); // Show permission screen again if denied
      
      const errorName = getErrorName(error);
      if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
        setPermissionDenied(true);
      } else {
        toast.error(t('camera.cameraAccessFailed'));
      }
    }
  }, [isAndroid, t]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraActive(false);
  }, [stream]);

  // V150.31: Fix dark photo capture - proper canvas drawing
  // V167: Made async to support WebP quality optimization
  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || isProcessing) return;

    setIsProcessing(true);
    console.log('📸 V167: Capturing photo...');

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('❌ V150.31: Canvas context failed');
      setIsProcessing(false);
      return;
    }

    // The saved bitmap is 4:5 portrait because the visible guide is 4:5.
    const targetWidth = PROFILE_PHOTO_WIDTH;
    const targetHeight = PROFILE_PHOTO_HEIGHT;

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    try {
      // V150.31: Get video dimensions
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      console.log('📐 V150.31: Video dimensions:', videoWidth, 'x', videoHeight);

      if (videoWidth === 0 || videoHeight === 0) {
        console.error('❌ V150.31: Invalid video dimensions');
        setIsProcessing(false);
        return;
      }

      const cropFrame = cropFrameRef.current;
      if (!cropFrame) {
        throw new Error('Crop guide is unavailable');
      }

      const videoBounds = video.getBoundingClientRect();
      const frameBounds = cropFrame.getBoundingClientRect();
      const facingMode = stream?.getVideoTracks()[0]?.getSettings()?.facingMode;
      const mirrored = isMirroredPreview(facingMode);
      const sourceCrop = calculateCoverSourceCrop({
        videoWidth,
        videoHeight,
        videoRect: {
          x: videoBounds.left,
          y: videoBounds.top,
          width: videoBounds.width,
          height: videoBounds.height,
        },
        // Border box of the visible mask — same rectangle the user framed.
        frameRect: {
          x: frameBounds.left,
          y: frameBounds.top,
          width: frameBounds.width,
          height: frameBounds.height,
        },
        mirrored,
      });

      // V150.31: Clear canvas first (prevent dark artifacts)
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, targetWidth, targetHeight);

      // Invert only when the live preview is actually mirrored (front-facing selfie).
      if (mirrored) {
        ctx.translate(targetWidth, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(
        video,
        sourceCrop.x,
        sourceCrop.y,
        sourceCrop.width,
        sourceCrop.height,
        0,
        0,
        targetWidth,
        targetHeight
      );
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      debugLog('✅ V276.9: Image captured to canvas', {
        targetWidth,
        targetHeight,
        mirrored,
        facingMode,
        sourceCrop,
        platform: Capacitor.getPlatform()
      });

      // V242_CAM: Onboarding capture MUST emit a File + blob preview, no upload here.
      const isOnboardingUpload = !!userNameProp;
      if (isOnboardingUpload && onImageCaptured) {
        const jpegBlob: Blob | null = await new Promise((resolve) =>
          canvas.toBlob(resolve, 'image/jpeg', 0.92)
        );
        const finalBlob: Blob | null = jpegBlob
          ? jpegBlob
          : await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

        if (!finalBlob) {
          throw new Error('Failed to create image blob');
        }

        const file = new File(
          [finalBlob],
          finalBlob.type === 'image/png' ? 'profile.png' : 'profile.jpg',
          { type: finalBlob.type }
        );
        const previewUrl = URL.createObjectURL(file);

        console.log('V242_CAM', {
          isOnboardingUpload,
          blobType: finalBlob.type,
          blobSize: finalBlob.size,
          fileType: file.type,
          fileSize: file.size,
          previewIsBlob: previewUrl.startsWith('blob:'),
        });

        onImageCaptured(file, previewUrl);
        stopCamera();
        onSkip();
        return;
      }

      // V276.10: Profile update flow - call onFileSelected to open replace dialog
      if (onFileSelected) {
        console.log('[V276.10] Profile flow detected (onFileSelected exists)');
        
        // Create JPEG blob for the replace dialog
        const jpegBlob: Blob | null = await new Promise((resolve) =>
          canvas.toBlob(resolve, 'image/jpeg', 0.92)
        );

        if (!jpegBlob) {
          console.error('[V276.10] Failed to create JPEG blob');
          setIsProcessing(false);
          return;
        }

        // Create file from blob
        const file = new File([jpegBlob], 'selfie.jpg', { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(file);
        
        // Create dataUrl directly from canvas (no FileReader needed!)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

        console.log('[V276.10] Calling onFileSelected with:');
        console.log('[V276.10] - file:', file.name, file.size);
        console.log('[V276.10] - previewUrl:', previewUrl.substring(0, 50));
        console.log('[V276.10] - dataUrl length:', dataUrl.length);

        // Stop camera and call onFileSelected to open replace dialog
        stopCamera();
        onFileSelected(file, previewUrl, dataUrl);
        onSkip();
        return;
      }

      // Fallback: Non-profile flow - keep existing WebP review screen
      const tryConvertWithQuality = (quality: number): Promise<Blob | null> => {
        return new Promise((resolve) => {
          canvas.toBlob(
            (blob) => {
              resolve(blob);
            },
            'image/webp',
            quality
          );
        });
      };

      // V167: Try different quality levels to get under 100KB
      let finalBlob: Blob | null = null;
      let quality = 0.85; // Start with high quality

      finalBlob = await tryConvertWithQuality(quality);

      if (finalBlob && finalBlob.size > 100 * 1024) {
        console.log('⚠️ V167: File > 100KB, reducing quality...');
        quality = 0.75;
        finalBlob = await tryConvertWithQuality(quality);
      }

      if (finalBlob && finalBlob.size > 100 * 1024) {
        console.log('⚠️ V167: File still > 100KB, further reduction...');
        quality = 0.65;
        finalBlob = await tryConvertWithQuality(quality);
      }

      if (finalBlob && finalBlob.size > 100 * 1024) {
        console.log('⚠️ V167: File still > 100KB, final reduction...');
        quality = 0.55;
        finalBlob = await tryConvertWithQuality(quality);
      }

      if (!finalBlob) {
        console.error('❌ V167: Failed to create WebP blob');
        setIsProcessing(false);
        return;
      }

      // V167: Convert blob to data URL for preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const photoDataUrl = reader.result as string;

        console.log('✅ V167: Photo captured as WebP', {
          size: `${Math.round(finalBlob!.size / 1024)}KB`,
          quality: quality,
          under100KB: finalBlob!.size <= 100 * 1024
        });

        setCapturedImageData(photoDataUrl);
        setIsInCaptureMode(true);
      };
      reader.readAsDataURL(finalBlob);
    } catch (error) {
      console.error('❌ V150.31: Capture failed:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, userNameProp, onImageCaptured, onFileSelected, stopCamera, onSkip, stream]);

  // V145.4: NUCLEAR RACE CONDITION PREVENTION - Ensure user record exists first
  // Added timeout and cancel support
  const handleSavePhoto = useCallback(async () => {
    if (!capturedImageData || !user?.id) {
      toast.error(t('camera.noPhotoOrUser'));
      return;
    }

    setIsProcessing(true);
    uploadAbortController.current = new AbortController();
    const abortSignal = uploadAbortController.current.signal;
    
    console.log('⚡ V145.4: Starting NUCLEAR race-proof save process with timeout...');

    // NUCLEAR STEP 0: Verify user record exists and is complete BEFORE upload
    // V242: During onboarding, user row may not exist yet. Allow upload and let onboarding insertion persist photo_urls.
    const isOnboardingUpload = !!userNameProp;

    try {
      const { supabase } = await import('../integrations/supabase/client');
      const { data: userCheck, error: userCheckError } = await supabase
        .from('users')
        .select('id, email, name, created_at')
        .eq('id', user.id)
        .maybeSingle();

      if ((userCheckError || !userCheck) && !isOnboardingUpload) {
        console.error('❌ V145.4: User record not found - cannot upload photo yet');
        toast.error(t('camera.completeProfileFirst'));
        setIsProcessing(false);
        return;
      }

      if (userCheck && (!userCheck.name || !userCheck.email) && !isOnboardingUpload) {
        console.error('❌ V145.4: User record incomplete - missing required fields');
        toast.error(t('camera.completeProfileInformation'));
        setIsProcessing(false);
        return;
      }

      if (!userCheck && isOnboardingUpload) {
        console.log('✅ V242: Onboarding upload - user row not created yet, proceeding');
      } else {
        console.log('✅ V145.4: User record verified - proceeding with upload');
        console.log('✅ V145.4: User name:', userCheck?.name);
        console.log('✅ V145.4: User created:', userCheck?.created_at);
      }
    } catch (error) {
      if (!isOnboardingUpload) {
        console.error('❌ V145.4: User verification failed:', error);
        toast.error(t('camera.verifyAccountFailed'));
        setIsProcessing(false);
        return;
      }
      console.warn('⚠️ V242: Onboarding upload - user verification check failed, proceeding anyway');
    }

    try {
      // V207.24: Get user name for upload
      // Priority: 1) userNameProp (from onboarding Step 1), 2) DB name, 3) fallback 'User'
      console.log('🔍 V207.24: userNameProp received:', userNameProp);
      let userName = userNameProp; // V207.24: Use prop if provided (onboarding case)

      if (!userName) {
        // V207.24: Fallback to DB if no prop provided (profile update case)
        console.log('⚠️ V207.24: No userName prop provided, fetching from DB');
        const { supabase } = await import('../integrations/supabase/client');
        const { data: userCheck } = await supabase
          .from('users')
          .select('name')
          .eq('id', user.id)
          .maybeSingle(); // V207.24: Use maybeSingle to prevent 406 errors

        userName = userCheck?.name || 'User';
        console.log('📋 V207.24: Fetched userName from DB:', userName);
      } else {
        console.log('✅ V207.24: Using userName from prop (onboarding context):', userName);
      }

      console.log('📋 V242: Final userName for Cloudinary display name:', userName);

      // Convert data URL to blob
      const response = await fetch(capturedImageData);
      const blob = await response.blob();
      const file = new File([blob], 'profile.webp', { type: 'image/webp' });
      const previewUrl = URL.createObjectURL(file);
      
      console.log(`✅ V242: Photo blob size: ${Math.round(blob.size / 1024)} KB`);
      
      // V242: For onboarding, pass captured image to PhotosStep WITHOUT uploading
      // PhotosStep will show dialog FIRST, then upload on confirm (SAME as file upload)
      if (isOnboardingUpload && onImageCaptured) {
        console.log('✅ V242: Onboarding mode - passing captured image to PhotosStep (NO upload yet)');
        console.log('✅ V242: PhotosStep will show dialog, upload on confirm (SAME as file upload)');
        onImageCaptured(file, previewUrl);
        setIsProcessing(false);
        return;
      }

      // V242: For non-onboarding (profile updates), upload immediately
      console.log('✅ V242: Profile update mode - uploading immediately');
      
      // Use the SAME service as file upload - this handles onboarding DB updates correctly
      const result = await fixedUnifiedPhotoService.uploadPhoto(
        user.id,
        file,
        (progress) => {
          console.log('📊 V242: Upload progress:', progress);
        },
        userName // V242: Pass userName from prop (onboarding context)
      );

      if (!result.success || !result.photoUrl) {
        const errorMsg = result.error || 'Upload failed';
        console.error('❌ V242: Upload failed:', errorMsg);
        URL.revokeObjectURL(previewUrl);
        throw new Error(errorMsg);
      }

      const cloudinaryUrl = result.photoUrl;
      console.log('✅ V265.3: Upload successful via fixedUnifiedPhotoService!');
      console.log('✅ V265.3: Cloudinary URL:', cloudinaryUrl);

      // V265.3: Use clean URL without cache-buster
      // Cloudinary handles caching correctly - no need for cache-busting
      updateUserPhoto(user.id, cloudinaryUrl);
      console.log('💾 V265.3: Cache updated with clean Cloudinary URL');

      // V265.3: Database persistence is now handled by fixedUnifiedPhotoService.uploadPhoto()
      // which calls uploadAndSaveProfilePhoto -> updateUserProfilePhoto
      // updateUserProfilePhoto already skips DB update during onboarding (user doesn't exist)
      console.log('✅ V265.3: Upload complete - DB update handled by unified service');

      // V253: Clean up temporary preview URL (safe to revoke - this is NOT the PhotoStore blob)
      // PhotoStore blob is managed by centralized objectUrl manager and persists for UI display
      URL.revokeObjectURL(previewUrl);

      // V265.3: Success - pass clean URL (no cache-buster)
      onComplete(cloudinaryUrl);
      console.log('🎉 V265.3: Selfie saved with clean Cloudinary URL!');

    } catch (error: unknown) {
      console.error('❌ V145.4: Save process failed:', error);
      const errorMessage = getErrorMessage(error);

      // Check if cancelled
      if (errorMessage.includes('cancelled') || abortSignal.aborted) {
        console.log('⚠️ Save cancelled by user');
        toast.info(t('camera.uploadCancelled'));
        return; // Don't show error for cancellation
      }

      // V145.4: NUCLEAR ERROR HANDLING - Provide specific guidance
      if (errorMessage.includes('timeout')) {
        toast.error(t('camera.uploadTimedOut'));
      } else if (errorMessage.includes('User record not found')) {
        toast.error(t('camera.completeProfileFirst'));
      } else if (errorMessage.includes('Database')) {
        toast.error(t('camera.databaseError'));
      } else {
        toast.error(t('camera.savePhotoFailed'));
      }
    } finally {
      setIsProcessing(false);
      uploadAbortController.current = null;
    }
  }, [capturedImageData, user?.id, onComplete, updateUserPhoto, onImageCaptured, userNameProp, t]);
  // V151.8: Convert to WebP helper function
  const convertToWebP = useCallback(async (blob: Blob): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        canvas.width = PROFILE_PHOTO_WIDTH;
        canvas.height = PROFILE_PHOTO_HEIGHT;

        ctx?.drawImage(img, 0, 0, PROFILE_PHOTO_WIDTH, PROFILE_PHOTO_HEIGHT);

        canvas.toBlob(
          (webpBlob) => {
            if (webpBlob) resolve(webpBlob);
            else reject(new Error('Conversion failed'));
          },
          'image/webp',
          0.85 // 85% quality
        );
      };

      img.onerror = () => reject(new Error('Image load failed'));
      img.src = URL.createObjectURL(blob);
    });
  }, []);





  // V276.9: Retake functionality - same for Android and web/iOS
  const handleRetake = useCallback(() => {
    console.log('🔄 V276.9: Retaking photo...');
    setCapturedImageData(null);
    setIsInCaptureMode(false);

    // V276.9: Android now uses same flow as web - restart camera stream
    if (isAndroid) {
      console.log('🎯 V276.9: Android retake - restarting camera stream');
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      // Return to permission screen to restart the flow
      setShowPermissionScreen(true);
      return;
    }

    // Web/iOS: Restart camera stream
    if (stream) {
      console.log('🛑 ProfilePhotoCapture: Stopping existing stream before restart');
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }

    // Reset video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    // Restart camera after cleanup
    setIsCameraActive(true);
  }, [stream, isAndroid]);

  // Cancel upload handler
  const handleCancelUpload = useCallback(() => {
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
      console.log('⚠️ Upload cancelled by user');
    }
    setIsProcessing(false);
    // Stay on review screen so user can retry
  }, []);

  // V276.9: Cleanup camera on unmount (works for Android and web/iOS now)
  useEffect(() => {
    return () => {
      // V276.9: Now Android also uses stream, so always cleanup
      stopCamera();
      if (uploadAbortController.current) {
        uploadAbortController.current.abort();
      }
    };
  }, [stopCamera]);
  
  // V276.9: Start camera when active (web/iOS only - Android starts in requestCameraPermissionAndStart)
  useEffect(() => {
    // V276.9: Only for web/iOS - Android camera is started in requestCameraPermissionAndStart
    if (!isAndroid && isCameraActive && !stream) {
      const startWebCamera = async () => {
        try {
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'user',
              width: { ideal: 640 },
              height: { ideal: 480 }
            },
            audio: false
          });
          setStream(mediaStream);
          
          if (videoRef.current) {
            const video = videoRef.current;
            video.srcObject = mediaStream;
            console.log('✅ V276.9: Web camera stream assigned to video element');
            
            const handleLoadedMetadata = async () => {
              console.log('📹 V276.9: Video metadata loaded, starting playback');
              try {
                await video.play();
                console.log('✅ V276.9: Video playback started successfully');
              } catch (playError: unknown) {
                console.error('❌ V276.9: Video play() failed:', playError);
                toast.error(t('camera.cameraPreviewStartFailed'));
              }
            };
            
            const handleError = (error: Event) => {
              console.error('❌ V276.9: Video element error:', error);
              toast.error(t('camera.cameraPreviewFailed'));
            };
            
            video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
            video.addEventListener('error', handleError, { once: true });
          }
        } catch (error: unknown) {
          console.error('❌ V276.9: Web camera access failed:', error);
          setShowPermissionScreen(true);
          setIsCameraActive(false);
          const errorName = getErrorName(error);
          if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
            setPermissionDenied(true);
          } else {
            toast.error(t('camera.cameraAccessFailed'));
          }
        }
      };
      startWebCamera();
    }
    // V276.9: Cleanup not needed here - handled by main cleanup effect
  }, [isAndroid, isCameraActive, stream, t]);

  // V141.22: Graceful profile photo loading with 404 handling
  useEffect(() => {
    const loadCurrentPhoto = async () => {
      if (user?.id) {
        try {
          const photoUrl = fixedUnifiedPhotoService.getCachedPhoto(user.id);
          if (photoUrl) {
            // V265.3: Use clean URL without cache-buster
            // V245: Never modify blob: or data: URLs (breaks them)
            const cleanUrl = photoUrl;

            // V265.3: Graceful image preload with 404 handling
            const img = new Image();
            img.onload = () => {
              console.log('V265.3: Current photo loaded successfully');
              setCurrentPhotoUrl(cleanUrl);
            };
            img.onerror = () => {
              console.log('V265.3: Current photo not found, clearing display gracefully');
              setCurrentPhotoUrl(null); // Clear broken image gracefully
            };
            img.src = cleanUrl;
          } else {
            setCurrentPhotoUrl(null);
          }
        } catch (error) {
          console.log('V141.22: Photo loading handled gracefully:', error.message);
          setCurrentPhotoUrl(null); // Graceful fallback
        }
      }
    };

    loadCurrentPhoto();
  }, [user?.id, photoVersion, refreshTrigger]);

  // V141.16: Original event listener for profile updates
  useEffect(() => {
    const handleProfileUpdate = (event: CustomEvent) => {
      if (event.detail?.userId === user?.id) {
        setCurrentPhotoUrl(event.detail.photoUrl);
        setRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener('v141-profile-updated', handleProfileUpdate as EventListener);
    return () => {
      window.removeEventListener('v141-profile-updated', handleProfileUpdate as EventListener);
    };
  }, [user?.id]);

  // Show permission screen first
  if (showPermissionScreen) {
    return (
      <SelfiePermissionScreen
        onContinue={requestCameraPermissionAndStart}
        onSkip={onSkip}
        permissionDenied={permissionDenied}
      />
    );
  }

  // V150.30: Show capture review with the same 4:5 framing as the camera guide
  if (isInCaptureMode && capturedImageData) {
    return (
      <div dir={direction} className="fixed inset-0 z-50 bg-black overflow-hidden flex items-center justify-center pt-[env(safe-area-inset-top,0px)]">
        {/* Preview - centered 4:5 portrait */}
        <div className="relative w-full max-w-md aspect-[4/5]">
          <img
            src={capturedImageData}
            alt={t('camera.capturedPhotoAlt')}
            className="w-full h-full object-cover rounded-lg"
            onLoad={() => {
              console.log('🖼️ Preview showing 4:5 portrait selfie');
            }}
          />
          
          {/* Processing overlay */}
          {isProcessing && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-white font-medium text-start">{t('camera.savingPhoto')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Review buttons */}
        <div className="absolute bottom-8 start-0 end-0 flex justify-center items-center gap-4 px-6 z-10 pb-[env(safe-area-inset-bottom,0px)]">
          <button
            onClick={handleRetake}
            disabled={isProcessing}
            className="w-16 h-16 rounded-full bg-gray-800/80 hover:bg-gray-700/80 backdrop-blur-sm flex items-center justify-center transition-colors shadow-lg disabled:opacity-50"
            aria-label={t('camera.retakePhoto')}
          >
            <RotateCcw className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={handleSavePhoto}
            disabled={isProcessing}
            className="w-20 h-20 rounded-full bg-purple-500 hover:bg-purple-600 flex items-center justify-center transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('camera.savePhoto')}
          >
            {isProcessing ? (
              <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Check className="w-8 h-8 text-white" />
            )}
          </button>

          <button
            onClick={isProcessing ? handleCancelUpload : onSkip}
            disabled={false} // Always allow cancel, even during processing
            className="w-16 h-16 rounded-full bg-gray-800/80 hover:bg-gray-700/80 backdrop-blur-sm flex items-center justify-center transition-colors shadow-lg"
            aria-label={isProcessing ? t('camera.cancelUpload') : t('common.cancel')}
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>
    );
  }

  // Camera view - the 4:5 guide is also the exact canvas crop on every platform.
  // V276.9: REMOVED the isAndroid return null check - Android now uses same video preview UI
  return (
    <div dir={direction} className="fixed inset-0 z-50 bg-black overflow-hidden pt-[env(safe-area-inset-top,0px)]">
      {/* Fullscreen video background */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="video-mirror absolute inset-0 w-full h-full object-cover object-center"
        onLoadedMetadata={async () => {
          console.log('✅ Video metadata loaded');
          // Ensure playback starts
          if (videoRef.current) {
            try {
              await videoRef.current.play();
              console.log('✅ Video playback confirmed');
            } catch (error) {
              console.error('❌ Video play() failed:', error);
            }
          }
        }}
      />

      {/* 4:5 portrait crop guide overlay — matches the 1080×1350 canvas */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
        {/* The spread shadow masks only the area outside the crop. */}
        <div
          ref={cropFrameRef}
          role="img"
          aria-label={t('camera.cropGuide')}
          className="relative box-border w-[80%] max-w-[360px] aspect-[4/5] bg-transparent border-4 border-white rounded-lg"
          style={{
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6), 0 0 20px rgba(255, 255, 255, 0.3)'
          }}
        >
          {/* Corner marks for visual guide */}
          <div className="absolute -top-1 -start-1 w-6 h-6 border-t-4 border-s-4 border-white rounded-ss-lg"></div>
          <div className="absolute -top-1 -end-1 w-6 h-6 border-t-4 border-e-4 border-white rounded-se-lg"></div>
          <div className="absolute -bottom-1 -start-1 w-6 h-6 border-b-4 border-s-4 border-white rounded-es-lg"></div>
          <div className="absolute -bottom-1 -end-1 w-6 h-6 border-b-4 border-e-4 border-white rounded-ee-lg"></div>
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />

      {/* BUTTONS AT BOTTOM - Circular, easy to reach */}
      <div className="absolute bottom-8 start-0 end-0 flex justify-center items-center gap-6 px-6 z-20 pb-[env(safe-area-inset-bottom,0px)]">
        {/* Cancel - LEFT */}
        <button
          onClick={onSkip}
          className="w-16 h-16 rounded-full bg-gray-800/80 hover:bg-gray-700/80 backdrop-blur-sm flex items-center justify-center transition-colors shadow-lg"
          aria-label={t('common.cancel')}
        >
          <X className="w-7 h-7 text-white" />
        </button>

        {/* Capture - CENTER */}
        <button
          onClick={capturePhoto}
          disabled={!stream || isProcessing}
          className="w-20 h-20 rounded-full bg-white hover:bg-gray-100 border-4 border-purple-500 flex items-center justify-center transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('camera.takePhoto')}
        >
          <Camera className="w-10 h-10 text-purple-500" />
        </button>
      </div>
    </div>
  );
};

export default ProfilePhotoCapture;
